/* eslint-disable eqeqeq */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetCmsComponentsStripQuery,
  useGetDrainPanTypeClassQuery,
} from 'app/services/ahuBoardFormsApi';
import { useGetAhuUnitQuery } from 'app/services/ahuBoardApi';
import { handleTabToNext } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import { accList, ABareInput, AccSubtab, ASelect } from '../accessoryFieldKit';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — DRAIN PAN form (accessories/drain_pan/{form.js, form.tmpl},
 * read in full). NO position — the drawDrainPanOnCanvas dim-snapshot path
 * (§49.6:2090-2116, chassis-owned); socket `ahuaccessorydrainpan` put refresh +
 * `changeDrainPan`/tunnel-width listeners (chassis); dim1_length commits carry
 * the BE `{select:false}` flag (§51.2 — chassis commitModelField special).
 * Tmpl census: subtabs = properties (unlabeled) + mounting only; the remarks
 * block is COMMENTED OUT in the tmpl (config key is dead).
 *
 * dev DELTA (drain-pan accessory overhaul) ported here:
 *  - Type select (drain_pan_type_id) — options from
 *    `…/ahu/drain_pan/type/class/?construction_class_id=<cc>&section_id=<component_id>`
 *    (form.js:36; cc from the AHU unit, component_id from the section's
 *    components strip row matched by section.type — stateStorage components).
 *  - params/ now REQUIRES ?type_id (400 without it). Picking a Type drives
 *    setCmsURLOverride('…/drain_pan/params/?type_id=<id>') which re-fetches
 *    cms_accessory = params.drain_pan — restoring Material/Connection/Conn-Hand
 *    AND providing the per-type Connection Position list (legacy
 *    listenTo change:drain_pan_type_id → re-GET params).
 *  - Connection Position select (conn_pos_id → serializer conn_position_id),
 *    options from cms_accessory.conn_pos; Conn Distance from Ups
 *    (dp_conn_distance_from_ups) — disabled UNLESS conn_pos_id is the manual
 *    position (id 4, server-computes the rest; form.tmpl:70).
 */
const config = ACCESSORY_FORM_CONFIGS.drain_pan;

/** form.tmpl:70 — the "manual" connection position (the others are
 * server-computed in AHUAccessoryDrainPan.save()); only it enables the
 * dp_conn_distance_from_ups input. */
const MANUAL_CONN_POS_ID = 4;

const DrainPanForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting'],
  });
  const { model, setCmsURLOverride } = api;

  // ---- construction_class_id (AHU unit) + section component_id (components
  // strip), mirroring legacy DrainPanView (form.js:34-36) ----
  const { ahuId: ahuIdParam } = useParams();
  const ahuId = Number(ahuIdParam);
  const { data: ahuUnit } = useGetAhuUnitQuery(Number.isFinite(ahuId) ? ahuId : skipToken);
  const constructionClassId = ahuUnit?.construction_class_id ?? 1; // "Standard" default
  const { data: componentsStrip } = useGetCmsComponentsStripQuery();
  const sectionComponentId = useMemo(() => {
    const match = (componentsStrip ?? []).find(
      (c) => String(c.value ?? '') === String(section.type ?? '')
    );
    return match ? Number(match.component_id) : null;
  }, [componentsStrip, section.type]);

  // Type options — GET drain_pan/type/class/?construction_class_id&section_id.
  const { data: drainPanTypes } = useGetDrainPanTypeClassQuery(
    { constructionClassId, sectionComponentId }
  );

  // ---- params/ re-fetch on Type change (legacy listenTo change:drain_pan_type_id
  // → re-GET params/?type_id=<id> → cms_accessory = params.drain_pan) ----
  const typeId = model?.get('drain_pan_type_id');
  useEffect(() => {
    if (typeId == null || typeId === '' || typeId === 'None') {
      setCmsURLOverride(null);
      return;
    }
    setCmsURLOverride(`/api/v1.0/cms/1/v1.0.0/ahu/drain_pan/params/?type_id=${typeId}`);
  }, [typeId, setCmsURLOverride]);

  // Material option text — form.tmpl:42 (selection mirror) and :46 (option) are
  // BOTH `{{#if label}}{{label}}{{else}}{{model}}{{/if}}`, i.e. the CMS row's
  // own label with the master part's model as fallback. The default optionLabel
  // chain (name→value→series→label→model) does NOT cover that:
  // CMSAHUDrainPanMtlGaugeSerializer (api/rest_cms/serializers.py:3899-3913)
  // ships BOTH `name` (the master part name, "Steel S-304 Solid 16ga") and
  // `label` (the CMS label, "16ga 304SS Solid"), and `name` wins the chain — so
  // dev3 rendered the part name where dev renders the label.
  const materialOptions = useMemo(
    () =>
      accList(api.cmsAccessory, 'mtl_gauge').map((o) => ({
        ...o,
        mtl_gauge_label: o.label || o.model,
      })),
    [api.cmsAccessory]
  );

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const panInter = !!model.get('pan_inter');
  const connPosManual = Number(model.get('conn_pos_id')) === MANUAL_CONN_POS_ID;

  return (
    <>
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="drain_pan" cid={cid} name="properties" sublistClass="max-height-auto">
            <li>
              <div>
                <input
                  name="tag"
                  className="model_field text-left"
                  type="text"
                  minLength={0}
                  maxLength={36}
                  defaultValue={String(model.get('tag') ?? '')}
                  key={`tag_${String(model.get('tag') ?? '')}`}
                  onBlur={(e) => api.commitModelField('tag', e.target.value)}
                />
                <i className="text-clear icon-close-1" />{' '}
                <span className="label">Tag</span>
              </div>
            </li>
            {/* Type — option label = the row's `type` name string
                ({{get_one type 'Type'}}, form.tmpl:14-23). Value = drain_pan_type_id.
                NO asterisk: the legacy `required` is on the <select> element
                (form.tmpl:15), while the label span is a bare `class="label"`
                (:21-23) — and it is the span's `required` class that draws the
                `*` (common.css `.label-part .required:after`). */}
            <ASelect
              api={api}
              name="drain_pan_type_id"
              label="Type"
              options={(drainPanTypes ?? []).map((t) => ({
                id: t.type_id as number,
                type: t.type,
              }))}
              labelKey="type"
              valueKey="id"
            />
            <li>
              {/* §JSX-WS — the legacy tmpl newlines between these controls are
                  4px of real layout (.unit/input are inline-block); dim1_length
                  additionally carries an inline `margin-left: 5px`
                  (form.tmpl:30). Without both, the pair drew ~9/13px left of
                  dev and its right edge missed the select column. */}
              <div className="length-dr">
                <span className="unit short-unit">in</span>{' '}
                <ABareInput
                  api={api}
                  name="dim1_length"
                  fieldClass="model_field medium-input"
                  min={6}
                  max={60}
                  style={{ marginLeft: 5 }}
                />{' '}
                <ABareInput
                  api={api}
                  name="drain_pan_inter_coil_downs_length"
                  fieldClass={`medium-input model_field${!panInter ? ' non-clickable' : ''}`}
                  max={60}
                  readOnly={!panInter}
                />{' '}
                <span className="label required">Pan Lower / Intermediate</span>
              </div>
            </li>
            {/* option label = label || model (tmpl :42-50) — see materialOptions. */}
            <ASelect
              api={api}
              name="mtl_gauge_id"
              label="Material"
              options={materialOptions}
              labelKey="mtl_gauge_label"
            />
            {/* Connection Position — conn_pos_id (serializer source conn_position_id);
                options from the re-fetched cms_accessory.conn_pos {id,name}
                (form.tmpl:53-66). NO asterisk — same as Type: `required` sits on
                the <select> (:56), the label span is bare (:62-64). */}
            <ASelect
              api={api}
              name="conn_pos_id"
              label="Connection Position"
              cmsPath="conn_pos"
              noneMode="visible"
            />
            {/* Conn Distance from Ups — DISTINCT from distance_from_section_ups;
                disabled unless conn_pos_id is the manual position (form.tmpl:67-75). */}
            <li>
              <div className="single-line">
                <span className="unit short-unit">in</span>{' '}
                <ABareInput
                  api={api}
                  name="dp_conn_distance_from_ups"
                  fieldClass="model_field"
                  readOnly={!connPosManual}
                />{' '}
                <span className="label">Connection Distance from Ups</span>
              </div>
            </li>
            <ASelect api={api} name="pipe_nipple_spn_id" label="Connection" cmsPath="pipe_nipple" hideNone={false} />
            {/* Conn Hand — options now per-type from the re-fetched
                cms_accessory.conn_hand {id,name} (form.tmpl:90-103). */}
            <ASelect api={api} name="conn_hand_id" label="Connection Hand" cmsPath="conn_hand" />
          </AccSubtab>

          <AccSubtab api={api} type="drain_pan" cid={cid} name="mounting" label="Mounting">
            <li>
              <div className="length-dr">
                <span className="unit short-unit">in</span>{' '}
                <ABareInput api={api} name="distance_from_section_ups" fieldClass="model_field" min={0} />{' '}
                <span className="label">From Upstream</span>
              </div>
            </li>
          </AccSubtab>
        </ul>
      </form>
    </>
  );
};

export default DrainPanForm;
